import { Controller } from "@hotwired/stimulus"
import { showToast } from "./toast"

export default class extends Controller {
  static targets = ["input", "preview", "placeholder"]
  static values = {
    apiKey: String,
    aspectRatio: Number,
    accept: { type: Array, default: [ "image/*" ] },
    maxSize: { type: Number, default: 0 }
  }

  connect() {
    if (!window.filestack && this.hasApiKeyValue) {
      this.loadFilestackScript()
    }
    this.dragCounter = 0
  }

  loadFilestackScript() {
    const script = document.createElement("script")
    script.src = "https://static.filestackapi.com/filestack-js/3.x.x/filestack.min.js"
    script.async = true
    document.head.appendChild(script)
  }

  open() {
    if (!window.filestack) {
      showToast("Filestack is not loaded. Please check your API key configuration.")
      return
    }

    const client = filestack.init(this.apiKeyValue)
    const acceptedTypes = this.acceptValue.length > 0 ? this.acceptValue : ["image/*"]
    const isImageOnly = acceptedTypes.length === 1 && acceptedTypes[0] === "image/*"
    const options = {
      accept: acceptedTypes,
      maxFiles: 1,
      // Crop transformations only make sense for images
      transformations: isImageOnly
        ? { crop: this.hasAspectRatioValue ? { aspectRatio: this.aspectRatioValue } : true }
        : {},
      onUploadDone: (result) => {
        if (result.filesUploaded.length > 0) {
          const file = result.filesUploaded[0]
          // For non-image uploads, dispatch a richer event so callers can use
          // metadata (filename, mimetype) — useful for the Resources modal
          // which auto-detects type from MIME.
          if (!isImageOnly) {
            this.dispatch("uploaded", { detail: { url: file.url, filename: file.filename, mimetype: file.mimetype, size: file.size } })
          }
          this.applyUpload(file.url)
        }
      }
    }

    client.picker(options).open()
  }

  dragenter(event) {
    event.preventDefault()
    this.dragCounter++
    event.currentTarget.classList.add("image-upload-preview--dragover")
  }

  dragover(event) {
    event.preventDefault()
  }

  dragleave(event) {
    event.preventDefault()
    this.dragCounter--
    if (this.dragCounter <= 0) {
      this.dragCounter = 0
      event.currentTarget.classList.remove("image-upload-preview--dragover")
    }
  }

  drop(event) {
    event.preventDefault()
    this.dragCounter = 0
    event.currentTarget.classList.remove("image-upload-preview--dragover")

    const file = event.dataTransfer?.files?.[0]
    if (!file) return

    // Validate against the configured accept types (defaults to image/*)
    if (!this.fileMatchesAccept(file)) {
      const allowedLabel = this.acceptValue.join(", ")
      showToast(`File type not allowed. Accepted: ${allowedLabel}`, "error")
      return
    }

    if (this.maxSizeValue > 0 && file.size > this.maxSizeValue) {
      const mb = Math.round(this.maxSizeValue / (1024 * 1024))
      showToast(`File too large. Max size: ${mb} MB.`, "error")
      return
    }

    this.uploadFile(file, event.currentTarget)
  }

  // Returns true if the dropped file matches one of the accept patterns.
  // Supports MIME wildcards like "image/*" and exact MIME like "application/pdf".
  fileMatchesAccept(file) {
    const accepts = this.acceptValue.length > 0 ? this.acceptValue : ["image/*"]
    return accepts.some((pattern) => {
      if (pattern.endsWith("/*")) return file.type.startsWith(pattern.slice(0, -1))
      return file.type === pattern
    })
  }

  async uploadFile(file, previewArea) {
    if (!window.filestack) {
      showToast("Filestack is not loaded. Please check your API key configuration.", "error")
      return
    }

    previewArea?.classList.add("image-upload-preview--uploading")

    try {
      const client = filestack.init(this.apiKeyValue)
      const result = await client.upload(file)
      // Dispatch a richer event for callers that need filename/MIME (e.g. the
      // Resources modal auto-detects type and sets a default title).
      this.dispatch("uploaded", { detail: { url: result.url, filename: result.filename, mimetype: result.mimetype, size: result.size } })
      this.applyUpload(result.url)
    } catch (error) {
      showToast("Upload failed. Please try again.", "error")
    } finally {
      previewArea?.classList.remove("image-upload-preview--uploading")
    }
  }

  applyUpload(url) {
    this.inputTarget.value = url

    if (this.hasPreviewTarget) {
      this.previewTarget.src = url
      this.previewTarget.classList.remove("hidden")
      this.previewTarget.style.removeProperty("display")
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("hidden")
    }

    const imageUploadEl = this.element.closest("[data-controller~='image-upload']")
      || this.element.querySelector("[data-controller~='image-upload']")
    if (imageUploadEl) {
      const imageUploadCtrl = this.application.getControllerForElementAndIdentifier(imageUploadEl, "image-upload")
      if (imageUploadCtrl) {
        imageUploadCtrl.hasImageValue = true
      }
    }
  }
}
